import { all, call, cancel, fork, put, take, takeLatest, select } from 'redux-saga/effects';
import {
  ATTACH_ROSTER,
  FETCH_EVENT,
  UPDATE_EVENT_LAYOUT,
  FETCH_EVENTS_AND_PRIORITIZE,
  FETCH_EVENTS_AND_PRIORITIZE_ERROR,
  DELETE_EVENT_OR_PRIORITIZE,
  DELETE_EVENT_OR_PRIORITIZE_SUCCESS
} from '../actions/actionTypes';
import {
  attachRosterDone,
  attachRosterError,
  attachRosterLoading,
  attachRosterSuccess,
  fetchEventDetailsDone,
  fetchEventDetailsError,
  fetchEventDetailsLoading,
  applyCachedEvent,
  storeEventCache,
  fetchEventDetailsSuccess,
  fetchEventIdeasError,
  fetchEventIdeasSuccess,
  fetchEventLayoutError,
  fetchEventLayoutSuccess,
  fetchEventPulsesError,
  fetchEventPulsesSuccess,
  fetchEventRosterError,
  fetchEventRosterSuccess,
  fetchEventSentimentByRatingError,
  fetchEventSentimentByRatingSuccess,
  fetchEventStatsError,
  fetchEventStatsSuccess,
  updateEventLayoutDone,
  updateEventLayoutError,
  updateEventLayoutLoading,
  updateEventLayoutSuccess,
  fetchEventsAndPrioritizeStart,
  fetchEventsAndPrioritizeLoading,
  fetchEventsAndPrioritizeError,
  fetchEventsAndPrioritizeSuccess,
  fetchEventsAndPrioritizeDone,
  syncFetchEventsAndPrioritizeStart,
  syncFetchEventsAndPrioritizeLoading,
  syncFetchEventsAndPrioritizeError,
  syncFetchEventsAndPrioritizeSuccess,
  syncFetchEventsAndPrioritizeDone,
  fetchEventStatsStart,
  fetchEventStatsDone,
  fetchEventStatsNewSuccess,
  deleteEventOrPrioritizeStart,
  deleteEventOrPrioritizeSuccess,
  deleteEventOrPrioritizeError,
  // deleteEventOrPrioritizeDone,
} from '../actions/event';
import { eventApi } from '../api/EventApi';
import Event from '../models/Event';
import { collectionApi } from '../api/CollectionApi';
import prioritizeAPI from '../api/v2/CollectionApi';
import Collection from '../models/Collection';
import { EventQuestionType } from '../models/EventQuestionType';
import { ideaApi } from '../api/IdeaApi';
import { ObjectUtils } from '../util/ObjectUtils';
import { documentsApi } from '../api/DocumentsApi';
import Logger from '../util/Logger';

// Create your Saga here...

// The API stores the count for each choice and the event-wide denominator
// separately. If the event aggregate is stale, choice_count is still correct
// while choice_freq/choice_pct are returned as zero. Derive the display values
// from the option counts so reports do not hide valid results.
const normalizeChoiceStats = (ideas) => {
  const totalChoices = ideas.reduce((total, idea) => {
    const count = Number(idea && idea.choice_count);
    return total + (Number.isFinite(count) && count > 0 ? count : 0);
  }, 0);

  if (totalChoices <= 0) {
    return ideas;
  }

  return ideas.map(idea => {
    const count = Number(idea && idea.choice_count);
    const normalizedCount = Number.isFinite(count) && count > 0 ? count : 0;
    const frequency = normalizedCount / totalChoices;

    return {
      ...idea,
      choice_freq: frequency,
      choice_pct: `${Math.round(frequency * 100)}%`
    };
  });
};

function* eventDetailsFlow() {
  const TTL_MS = 10 * 60 * 1000; // 10 min
  let task = null;
  
    while (true) {
    const { eventId, forceRefresh = false } = yield take(FETCH_EVENT);
    const state = yield select();
    const cacheEntry = state.event?.cache?.[eventId];
    const isFresh = cacheEntry && (Date.now() - cacheEntry.fetchedAt) < TTL_MS;
    if (isFresh && !forceRefresh) {
      console.log('eventSaga: Cache hit – using cached event for', eventId);
      yield put(applyCachedEvent(eventId, cacheEntry.data));
      continue; // skip network fetch
    }
    if (!!task) {
      Logger.info('Cancelling old details fetch');
      yield cancel(task);
      task = null;
    }
    task = yield fork(fetchEventDetailsSaga, eventId);
  }
}

function* fetchEventDetailsSaga(eventId) {
  try {
    yield put(fetchEventDetailsLoading());

    // Get the event
    const event = yield call(eventApi.getDetails, eventId);

    // Get the event layout and stats in parallel
    // These are not wrapped within their own try catches because we will handle the error
    // via a promise chain catch()
    const networkCalls = [
      call(documentsApi.getCreatorReportLayout, Event.getId(event)),
      call(eventApi.getStats, Event.getId(event)),
      call(eventApi.getPulseStats, Event.getId(event))
    ];

    // If its a scale type, it may have a roster breakdown
    let rosterIndex = -1;
    if (!!Event.getRosterId(event)) {
      networkCalls.push(call(eventApi.getSegmentationRoster, Event.getId(event)));
      rosterIndex = networkCalls.length - 1;

      Logger.info('Roster at index: ', rosterIndex);
    }

    let ideasIndex = -1;
    if (!EventQuestionType.isOpenEndedType(Event.getQuestionType(event))) {
      // If its not an open ended question, get the ideas list too
      networkCalls.push(call(ideaApi.getIdeas, Event.getId(event)));
      ideasIndex = networkCalls.length - 1;

      Logger.info('Ideas at index: ', ideasIndex);
    }

    let sentimentByRatingIndex = -1;
    if (Event.isSentimentEnabled(event) &&
      EventQuestionType.isScaleType(Event.getQuestionType(event))) {
      // If its a scale type, it will have sentiment by rating
      networkCalls.push(call(eventApi.getSentimentByRating, Event.getId(event)));
      sentimentByRatingIndex = networkCalls.length - 1;

      Logger.info('Sentiment at index: ', sentimentByRatingIndex);
    }

    const results = yield all(networkCalls);

    Logger.info('Network calls', networkCalls, results);

    const { layoutError, ...layout } = results[0];
    const { statsError, ...stats } = results[1];
    const { pulseError, ...pulses } = results[2];

    // The layout API call returns an error object instead of throwing, so we extract it here
    // if it exists
    if (layoutError) {
      yield put(fetchEventLayoutError(layoutError));
    } else {
      yield put(fetchEventLayoutSuccess(layout));
    }

    let pulsesArray = null;
    let rosterArray = null;
    let ideasArray = null;
    let sentimentArray = null;
    // The stats API call returns an error object instead of throwing, so we extract it here
    // if it exists
    if (statsError) {
      yield put(fetchEventStatsError(statsError));
    } else {
      yield put(fetchEventStatsSuccess(stats));
    }

    // The pulses API call returns an error object instead of throwing, so we extract it here
    // if it exists
    if (pulseError) {
      yield put(fetchEventPulsesError(pulseError));
    } else {

      pulsesArray = ObjectUtils.toArray(pulses);
      yield put(fetchEventPulsesSuccess(pulsesArray));
    }

    if (rosterIndex >= 0) {
      Logger.info('Get roster at index: ', rosterIndex);
      const { rosterError, ...roster } = results[rosterIndex];
      if (rosterError) {
        yield put(fetchEventRosterError(rosterError));
      } else {

        // When we extract ideas here, it extracts as an Object not as an Array.
        // We map it back into an Array so that we do not go insane.
        const rosterArray = ObjectUtils.toArray(roster);
        yield put(fetchEventRosterSuccess(rosterArray));
      }
    }

    // If its not an open ended question, get the ideas list too
    if (ideasIndex >= 0) {
      Logger.info('Get ideas at index: ', ideasIndex);
      const { ideasError, ...ideas } = results[ideasIndex];

      if (ideasError) {
        yield put(fetchEventIdeasError(ideasError));
      } else {

        ideasArray = ObjectUtils.toArray(ideas);
        if (EventQuestionType.isBinaryType(Event.getQuestionType(event)) ||
          EventQuestionType.isMultipleType(Event.getQuestionType(event))) {
          ideasArray = normalizeChoiceStats(ideasArray);
        }
        yield put(fetchEventIdeasSuccess(ideasArray));
      }
    }

    // If its a scale question, get the sentiment by rating
    if (sentimentByRatingIndex >= 0) {
      Logger.info('Get sentiment at index: ', sentimentByRatingIndex);

      // Sentiment by rating can be null...
      const { sentimentByRatingError, ...sentimentByRating } = results[sentimentByRatingIndex] || {};

      if (sentimentByRatingError) {
        yield put(fetchEventSentimentByRatingError(sentimentByRatingError));
      } else {

        // When we extract ideas here, it extracts as an Object not as an Array.
        // We map it back into an Array so that we do not go insane.
        sentimentArray = ObjectUtils.toArray(sentimentByRating);
        yield put(fetchEventSentimentByRatingSuccess(sentimentArray));
      }
    }

    // If a collection exists, get it too
    const collectionJson = Event.getCollection(event);
    try {
      let collection = null;
      if (collectionJson) {
        collection = yield call(collectionApi.getDetails, Collection.getId(collectionJson));
      }

      // Build combined payload for cache
      const cachedData = {
        event,
        collection,
        stats,
        layout: layout.sections,
        pulseStats: pulsesArray,
        roster: rosterArray,
        ideas: ideasArray,
        sentimentByRating: sentimentArray
      };
      yield put(storeEventCache(eventId, cachedData, Date.now()));

      // Deliver the event and collection if it exists
      yield put(fetchEventDetailsSuccess(event, collection));
    } catch (error) {
      // Error getting collection details
      yield put(fetchEventDetailsError(null, error));
    }
  } catch (error) {
    // Error getting event details
    yield put(fetchEventDetailsError(error, null));
  } finally {
    yield put(fetchEventDetailsDone());
  }
}

function* watchUpdateEventLayoutSaga() {
  yield takeLatest(UPDATE_EVENT_LAYOUT, updateEventLayoutSaga);
}

function* updateEventLayoutSaga({ eventId, sections }) {
  try {
    yield put(updateEventLayoutLoading());
    // Fake the put before the network call so the page syncs up instantly
    yield put(updateEventLayoutSuccess({ sections: sections }));
    const layout = yield call(documentsApi.updateCreatorReportLayout, eventId, sections);
    // When the network call delivers, re-sync the page
    yield put(updateEventLayoutSuccess(layout));
  } catch (error) {
    yield put(updateEventLayoutError(error));
  } finally {
    yield put(updateEventLayoutDone());
  }
}

function* watchAttachRosterSaga() {
  yield takeLatest(ATTACH_ROSTER, attachRosterSaga);
}

function* attachRosterSaga({ eventId, rosterId }) {
  try {
    yield put(attachRosterLoading());

    // Get the refreshed event
    const event = yield call(eventApi.modifyRoster, eventId, rosterId);
    yield put(attachRosterSuccess(event));

  } catch (error) {
    yield put(attachRosterError(error));
  } finally {
    yield put(attachRosterDone());
  }

  const { rosterError, ...roster } = yield call(eventApi.getSegmentationRoster, eventId);
  if (rosterError) {
    yield put(fetchEventRosterError(rosterError));
  } else {

    // When we extract ideas here, it extracts as an Object not as an Array.
    // We map it back into an Array so that we do not go insane.
    const rosterArray = ObjectUtils.toArray(roster);
    yield put(fetchEventRosterSuccess(rosterArray));
  }
}

function* fetchEventsAndPrioritizeSaga() {
  yield put(fetchEventsAndPrioritizeStart());

  try {
    Logger.info('Begin fetching events');
    yield put(fetchEventsAndPrioritizeLoading());
    yield select((state) => state.user.user);

    const [events, collections] = yield all([
      call(eventApi.getDashboardEvents),
      call(prioritizeAPI.get, { query: '?type=prioritize' }),
    ]);

    const eventsCollections = [...events, ...collections];

    yield put(fetchEventsAndPrioritizeSuccess(eventsCollections));

    yield put(fetchEventsAndPrioritizeDone());
    yield fetchEventStatsSaga();
  } catch (error) {
    Logger.error('Error fetchEventsAndPrioritizeSaga: ', error);
    yield put(fetchEventsAndPrioritizeError(error));
  }
}

function* syncFetchEventsAndPrioritizeSaga() {
  yield put(syncFetchEventsAndPrioritizeStart());
  let collections = []

  try {
    Logger.info('Begin refetching events');
    yield put(syncFetchEventsAndPrioritizeLoading());
    yield select((state) => state.user.user);

    const events = yield call(eventApi.getDashboardEvents)
    const response = yield call(prioritizeAPI.get, { query: '?type=prioritize' })

    if (!response.collectionsError) {
      collections = response
    }

    const eventsCollections = [...events, ...collections];

    yield put(syncFetchEventsAndPrioritizeSuccess(eventsCollections));

    yield put(syncFetchEventsAndPrioritizeDone());
    yield fetchEventStatsSaga();
  } catch (error) {
    Logger.error('Error syncFetchEventsAndPrioritizeSaga: ', error);
    yield put(syncFetchEventsAndPrioritizeError(error));
  }
}

function* fetchEventStatsSaga() {
  yield put(fetchEventStatsStart());

  try {
    Logger.info('Begin fetching event stats');
    const events = yield select((state) =>
      state.event.events.filter((e) => !e.sample && e.question_type),
    );
    const collections = yield select((state) =>
      state.event.events.filter((e) => !e.sample && e.collection_type),
    );

    const eventsStats = yield all(events.map((e) => call(eventApi.getStats, e.id)));
    const prioritizeResults = yield all(collections.map((e) => call(prioritizeAPI.getResult, e.id)));

    const estatsObj = Object.fromEntries(
      events.map(({ id }, idx) => [id, [eventsStats[idx]]]),
    );
    const pstatsObj = Object.fromEntries(
      collections
        .map(({ id }, idx) => [id, prioritizeResults[idx]])
        .filter(([k, v]) => v.length),
    );

    yield put(fetchEventStatsNewSuccess({ ...estatsObj, ...pstatsObj }));

    yield put(fetchEventStatsDone());
  } catch (error) {
    Logger.error('Error fetchEventStatsSaga: ', error);
    yield put(fetchEventStatsError(error));
  }
}

function* deleteEventOrPrioritizeSaga() {
  yield put(deleteEventOrPrioritizeStart());
  const collection = yield select((state) => state.event.collectionToDelete);

  try {
    Logger.info('Begin deleteing eOrs');

    if (collection.type === 'prioritize') {
      yield call(prioritizeAPI.delete, collection.id)
    } else {
      yield call(eventApi.deleteCollection, collection.id)
    }

    yield put(deleteEventOrPrioritizeSuccess());

    // yield put(deleteEventOrPrioritizeDone());
  } catch (error) {
    Logger.error('Error deleteEventOrPrioritizeSaga: ', error);
    yield put(deleteEventOrPrioritizeError(error));
  }
}

function* watchFetchEventsAndPrioritizeSaga() {
  yield takeLatest([FETCH_EVENTS_AND_PRIORITIZE, DELETE_EVENT_OR_PRIORITIZE_SUCCESS], fetchEventsAndPrioritizeSaga);
}

function* watchSyncFetchEventsAndPrioritizeSaga() {
  yield takeLatest(FETCH_EVENTS_AND_PRIORITIZE_ERROR, syncFetchEventsAndPrioritizeSaga);
}

function* watchDeleteEventOrPrioritizeSaga() {
  yield takeLatest(DELETE_EVENT_OR_PRIORITIZE, deleteEventOrPrioritizeSaga);
}

export default [
  fork(watchUpdateEventLayoutSaga),
  fork(watchAttachRosterSaga),
  fork(watchFetchEventsAndPrioritizeSaga),
  fork(watchSyncFetchEventsAndPrioritizeSaga),
  fork(watchDeleteEventOrPrioritizeSaga),
  fork(eventDetailsFlow)
];
